import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate, useLocation } from 'react-router-dom';
import { APP_VERSION } from '../../version';
import MenuIcon from '@mui/icons-material/Menu';
import NotificacaoMenu from '../NotificacaoMenu';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  AdminPanelSettings,
  Paid
} from '@mui/icons-material';

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Clientes', icon: <PeopleIcon />, path: '/clientes' },
  { text: 'Regras de Juros', icon: <MoneyIcon />, path: '/regras-juros' },
  { text: 'Empréstimos', icon: <MoneyIcon />, path: '/emprestimos' },
  { text: 'Pagamentos', icon: <PaymentIcon />, path: '/pagamentos' },
  { text: 'Fluxo de Caixa', icon: <AccountBalanceIcon />, path: '/fluxo-caixa' },
  { text: 'Relatório de Juros Pagos', icon: <Paid />, path: '/relatorios/juros-pagos' },
  { text: 'Relatório de Atrasados', icon: <MoneyIcon />, path: '/relatorios/atrasados' },
  { text: 'Usuários', icon: <AdminPanelSettings />, path: '/usuarios' },
  { text: 'Logs de Atividades', icon: <SettingsIcon />, path: '/logs' },
  { text: 'Histórico de Notificações', icon: <NotificationsIcon />, path: '/notificacoes/historico' },
  { text: 'Configurar Notificações', icon: <SettingsIcon />, path: '/notificacoes/configuracoes' },
];

const Sidebar = () => {
  const { signOut } = useAuth();

  const handleLogout = async () => {
    await signOut();
    window.location.href = '/login';
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPath, setCurrentPath] = useState(location.pathname);
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isSelected = (path) => {
    if (path === '/' && currentPath === '/') return true;
    if (path !== '/' && currentPath.startsWith(path)) return true;
    return false;
  };

  const drawerContent = (
    <>
      <Box sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
          Sistema de Empréstimos
        </Typography>
        {!isMobile && <NotificacaoMenu />}
      </Box>
      <List sx={{ flex: 1, overflowY: 'auto', px: 1 }}>
        {menuItems.map((item) => {
          const selected = isSelected(item.path);

          return (
            <ListItem
              button
              key={item.text}
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
              sx={{
                borderRadius: '8px',
                mb: 0.5,
                backgroundColor: selected ? '#283593 !important' : 'transparent',
                color: selected ? '#ffffff !important' : '#e8eaf6',
                '&:hover': {
                  backgroundColor: selected ? '#3949ab !important' : 'rgba(255, 255, 255, 0.08)',
                },
                fontWeight: selected ? 'bold !important' : 'normal',
                transition: 'all 0.3s ease',
              }}
            >
              <ListItemIcon sx={{
                color: selected ? '#ffffff' : '#e8eaf6',
                minWidth: 40
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.95rem',
                  fontWeight: selected ? 'bold' : 'normal'
                }}
              />
            </ListItem>
          );
        })}
      </List>
      <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.12)' }}>
        <ListItem disablePadding>
          <ListItem button onClick={handleLogout} sx={{ color: '#ff1744', py: 1 }}>
            <ListItemIcon sx={{ color: '#ff1744', minWidth: 40 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Sair" />
          </ListItem>
        </ListItem>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Versão {APP_VERSION}
          </Typography>
        </Box>
      </Box>
    </>
  );

  return (
    <>
      {isMobile && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: '#1a237e',
          zIndex: 1100,
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center'
        }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ color: 'white' }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              ml: 2,
              color: 'white'
            }}
          >
            Sistema de Empréstimos
          </Typography>
          <NotificacaoMenu />
        </Box>
      )}

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={isMobile ? handleDrawerToggle : undefined}
        sx={{
          width: 240,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 240,
            boxSizing: 'border-box',
            backgroundColor: '#1a237e',
            color: 'white',
            height: '100vh',
            position: isMobile ? 'fixed' : 'static',
          },
        }}
      >
        {drawerContent}
      </Drawer>
      {isMobile && <Box sx={{ height: 56 }} />}
    </>
  );
};

export default Sidebar;